import { useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import {
  LayoutDashboard, FolderKanban, CheckSquare, Bell, ShieldCheck, Users, SlidersHorizontal,
  ListTree, ScrollText, LogOut, Menu, X, ChevronDown, UserCog, FileCheck2,
} from "lucide-react";
import clsx from "clsx";
import { useAuth } from "@/lib/auth";
import { useAppSettings } from "@/lib/lookups";
import { BrandLogo } from "./Logo";
import { NotificationBell } from "./NotificationBell";
import { TaskMenu } from "./TaskMenu";
import { ThemeMenu } from "./ThemeMenu";
import { Avatar } from "./ui";

interface NavItem { to: string; label: string; icon: any; perm?: string; role?: string }

const NAV: NavItem[] = [
  { to: "/dashboard", label: "Portfolio Dashboard", icon: LayoutDashboard, perm: "dashboard.portfolio" },
  { to: "/projects", label: "Projects", icon: FolderKanban },
  { to: "/my-tasks", label: "My Tasks", icon: CheckSquare },
  { to: "/approvals", label: "Approvals", icon: FileCheck2 },
  { to: "/notifications", label: "Notifications", icon: Bell },
];

const ADMIN_NAV: NavItem[] = [
  { to: "/admin/users", label: "Users", icon: Users, perm: "users.manage" },
  { to: "/admin/roles", label: "Roles & Permissions", icon: ShieldCheck, perm: "roles.manage" },
  { to: "/admin/lookups", label: "Dropdown Lists", icon: ListTree, perm: "lookups.manage" },
  { to: "/admin/custom-fields", label: "Custom Fields", icon: SlidersHorizontal, perm: "custom_fields.manage" },
  { to: "/admin/activity-log", label: "Activity Log", icon: ScrollText, perm: "activity_log.view" },
  { to: "/admin/settings", label: "Application Settings", icon: UserCog, perm: "platform.manage" },
];

export function Layout() {
  const { user, logout, can } = useAuth();
  const settings = useAppSettings();
  const nav = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenu, setUserMenu] = useState(false);

  const showAdmin = ADMIN_NAV.some((n) => !n.perm || can(n.perm));
  const appName = settings?.branding?.appName || "The Longevity PM";

  const SidebarInner = (
    <>
      <div className="px-4 py-4">
        <Link to="/" className="inline-block"><BrandLogo height={40} /></Link>
        <p className="mt-1.5 pl-1 text-[10px] uppercase tracking-[0.16em] text-ink-faint">Project Management</p>
      </div>
      <nav className="flex-1 space-y-0.5 overflow-y-auto px-3 pb-4">
        {NAV.filter((n) => !n.perm || can(n.perm)).map((n) => (
          <NavLink key={n.to} to={n.to} onClick={() => setMobileOpen(false)}
            className={({ isActive }) => clsx("nav-link", isActive && "nav-link-active")}>
            <n.icon size={17} /> {n.label}
          </NavLink>
        ))}
        {showAdmin && (
          <>
            <p className="px-3 pb-1 pt-5 text-[10px] font-bold uppercase tracking-widest text-ink-faint">Administration</p>
            {ADMIN_NAV.filter((n) => !n.perm || can(n.perm)).map((n) => (
              <NavLink key={n.to} to={n.to} onClick={() => setMobileOpen(false)}
                className={({ isActive }) => clsx("nav-link", isActive && "nav-link-active")}>
                <n.icon size={17} /> {n.label}
              </NavLink>
            ))}
          </>
        )}
      </nav>
      <div className="border-t border-line px-3 py-3 text-[11px] text-ink-faint">
        {appName} · v1.0
      </div>
    </>
  );

  return (
    <div className="flex h-dvh overflow-hidden bg-canvas">
      {/* Desktop sidebar */}
      <aside className="hidden w-64 shrink-0 flex-col border-r border-line bg-surface lg:flex">{SidebarInner}</aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 flex h-full w-64 flex-col bg-surface shadow-pop">
            <button className="btn-ghost absolute right-2 top-3 !p-1.5" onClick={() => setMobileOpen(false)}><X size={18} /></button>
            {SidebarInner}
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 shrink-0 items-center gap-2 border-b border-line bg-surface px-3 sm:px-5">
          <button className="btn-ghost !p-2 lg:hidden" onClick={() => setMobileOpen(true)}><Menu size={18} /></button>
          <div className="flex-1" />
          <TaskMenu />
          <NotificationBell />
          <ThemeMenu />
          <div className="relative">
            <button className="btn-ghost !gap-2 !px-1.5 !py-1" onClick={() => setUserMenu((o) => !o)}>
              <Avatar name={user?.fullName || "?"} color={user?.avatarColor} />
              <span className="hidden text-left sm:block">
                <span className="block text-sm font-medium leading-tight">{user?.fullName}</span>
                <span className="block text-[11px] leading-tight text-ink-faint">{user?.roles[0]?.name || "Member"}</span>
              </span>
              <ChevronDown size={14} className="text-ink-faint" />
            </button>
            {userMenu && (
              <div className="absolute right-0 z-40 mt-2 w-52 animate-fade-in rounded-xl border border-line bg-surface p-1.5 shadow-pop"
                onMouseLeave={() => setUserMenu(false)}>
                <div className="px-2.5 py-2 text-xs text-ink-faint">
                  Signed in as<br /><span className="font-medium text-ink">@{user?.username}</span>
                </div>
                <Link to="/profile" onClick={() => setUserMenu(false)} className="nav-link"><UserCog size={16} /> Profile & security</Link>
                <button onClick={() => { logout().then(() => nav("/login")); }} className="nav-link w-full text-st-delayed">
                  <LogOut size={16} /> Sign out
                </button>
              </div>
            )}
          </div>
        </header>

        <main className="min-h-0 flex-1 overflow-y-auto">
          <div className="mx-auto max-w-[1500px] px-3 py-5 sm:px-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
